import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import {Paper, Typography, Tooltip, IconButton, Fab} from '@material-ui/core';
import {BackButton, hasAtLeastOnePermission, hasPermission, localization} from '../../sc-utilities';
import MaterialTable from 'material-table';
import * as action from '../../np-core/Auth/store/authActions';
import {Add, Delete, Refresh} from '@material-ui/icons';
import Toolbar from '../../sc-utilities/Toolbar';
import { MuiTableRows } from '../../../themes';
import * as actions from './store/adminDiagnoseCaseActions';
import Export from "../../sc-components/ExportForm";
import * as Yup from "yup";
import ApiKeyDelete from "../../sc-settings/SecurazeAPI/ApiSetup/ApiKeyDelete";

const styles = theme => ({
    "@global": {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        },
    },
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    customTooltip: {
        marginLeft: -16
    },
    customToolbar: {
        marginTop: 9,
        marginLeft: 14,
        width: "900px"
    }
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];

const route = 'diagnose-case';

const AdminDiagnoseCaseList = props => {
    const { classes, translations, onFetchDiagnoseCases, onFetchDiagnoseCasesCount, saveColumnsFilter, activeCustomerID, tableColumns, tableRef, permissions, history, onBulkDeleteDiagnoseCases } = props;

    const [deleteModalState, setDeleteModalState] = useState({ open: false, ids: [] });

    const columnObject = useMemo(() => tableColumns, [tableColumns]);
    const windowHeight = 240;

    const selectionPermissions = hasAtLeastOnePermission(permissions, ['diagnose_case_delete']);

    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: false,
        selection: selectionPermissions,
        search: false,
        minBodyHeight: window.innerHeight - windowHeight < 300 ? 300 : window.innerHeight - windowHeight,
        maxBodyHeight: window.innerHeight - windowHeight,
        pageSizeOptions: rowsPageSizeInit,
        exportButton: false,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? MuiTableRows.odd : MuiTableRows.even };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false
    });

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    };

    async function fetchTableData(query) {
        let q = filterQuery(query.filters);
        query['ActiveCustomerID'] = activeCustomerID;
        query['q'] = q;
        let count = await onFetchDiagnoseCasesCount(query);
        let result = count ? await onFetchDiagnoseCases(query) : [];
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result, page: query.page, pageSize: pageSize, totalCount: count };
    };

    const initialValues = [
        'LongName',
        'ShortName',
        'ExecutionType',
        'ProductType',
        'Group',
        'Visible',
        'Key'
    ];

    const formSettings = {
        Validation: initialValues.reduce((o, k) => ({...o, [k]: Yup.boolean()}), {}),
        ButtonSubmit: hasPermission(permissions, "diagnose_case_export") ? {
            Title: translations["download"] || "Download",
            Color: 'primary'
        } : null
    };

    const tableTitle = () => {
        return <div className={classes.customToolbar}>
            <div className={classes.tableTitle}>
                {BackButton(translations, classes.customTooltip)}
                <Typography className={classes.typography}>{translations['diagnose_cases'] || 'Diagnose cases'}</Typography>
                {hasPermission(permissions, 'diagnose_case_create') &&
                <Fab
                    variant="extended"
                    size="small"
                    color="primary"
                    aria-label="Add"
                    style={{ borderRadius: '1px' }}
                    className={classes.fab}
                    onClick={() => history?.push(`/admin/diagnose-case/create`)}
                >
                    <Add size="small" />
                    {translations["create_new"] || "Create new"}
                </Fab>}
                <Tooltip title={translations["refresh"] || "Refresh"}>
                    <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                        <Refresh />
                    </IconButton>
                </Tooltip>
            </div>
            {
                hasPermission(permissions, 'diagnose_case_export') ?
                    <Export
                        route={route}
                        tableRef={tableRef}
                        initialValues={initialValues}
                        formSettings={formSettings}
                        showVisibilities={true}
                    /> : null
            }
        </div>
    };

    const columnHidden = newColumnState => {
        let objToSendColumn = {}
        let objColumns = {}
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden
            return objColumns
        })
        objToSendColumn[`AdminDiagnoseCase`] = objColumns
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomerID })
        return newColumnState
    };

    const handleBulkDelete = ids => {
        setDeleteModalState({ ...deleteModalState, open: true, ids });
    };

    const handleCloseSafe = () => {
        setDeleteModalState({ open: false, ids: [] });
    }

    const handleCloseDanger = async () => {
        await onBulkDeleteDiagnoseCases({ DiagnoseCaseIDs: deleteModalState.ids, ActiveCustomerID: activeCustomerID });
        setDeleteModalState({ open: false, ids: [] });
        tableRef.current && tableRef.current.onQueryChange()
    };

    const tableActions = [
        {
            tooltip: `${translations["remove_all_selected"] || "Remove all selected"} ${translations["diagnose_cases"] || 'Diagnose cases'}`,
            icon: <Delete />,
            onClick: (rowData) => handleBulkDelete(rowData.map(diagnoseCase => diagnoseCase.DiagnoseCaseID))
        },
    ];

    return (
        <Paper>
            <MaterialTable
                components={{
                    Toolbar: props => (
                        <Toolbar data={props} actions={tableActions} selectedRows={props.selectedRows} translations={translations} />
                    )
                }}
                localization={localization(translations)}
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                onChangeRowsPerPage={(size) => {
                    pageSize = size
                }}
                onChangeColumnHidden={columnHidden}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
            />
            {hasPermission(permissions, 'diagnose_case_delete') ? (
                <ApiKeyDelete
                    entityName={translations["diagnose_cases"] || 'Diagnose cases'}
                    translations={translations}
                    handleCloseDanger={handleCloseDanger}
                    handleCloseSafe={handleCloseSafe}
                    deleteModalState={deleteModalState}
                />
            ) : null}
        </Paper>
    );
};


const mapStateToProps = state => {
    return {
        activeCustomerID: state?.core?.login?.activeCustomer?.CustomerID,
        translations: state?.translate?.translations?.activeTranslations || {},
        deleting: state?.setting?.chassisTypes?.deleting,
        permissions: state?.core?.login?.permissions || []
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onFetchDiagnoseCasesCount: filter => dispatch(actions.fetchAdminDiagnoseCasesCount(filter)),
        onFetchDiagnoseCases: filter => dispatch(actions.fetchAdminDiagnoseCases(filter)),
        onBulkDeleteDiagnoseCases: ids => dispatch(actions.bulkDeleteAdminDiagnoseCases(ids)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data))
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(AdminDiagnoseCaseList));


